import React, { useRef, useState } from 'react'; // React 컴포넌트를 만들고, 상태(useState)와 참조(useRef)를 쓰기 위해 임포트
import '../styles/components/IDVerification.css';

const API_URL = process.env.REACT_APP_API_URL; // .env에 설정된 백엔드 API 주소(중복검사 요청을 보낼 때 사용)

function IDVerification({ formData, setFormData, idVerified, setIdVerified, loading, checkUrl }) { // 부모로부터 폼값/업데이트 함수, 검증상태, 로딩상태를 props로 받음
  const [isIdModalOpen, setIsIdModalOpen] = useState(false); // 모달(메시지 팝업) 열림 여부
  const [idModalMessage, setIdModalMessage] = useState(''); // 모달 안에 보여줄 텍스트
  const [idModalType, setIdModalType] = useState(''); // 모달 타입: 'success' 또는 'error'에 따라 버튼 구성이 달라짐
  const [checking, setChecking] = useState(false);    // 현재 중복검사 요청이 진행 중인지 표시(버튼 연타 방지)
  const abortRef = useRef(null);                      // 진행 중인 네트워크 요청을 취소하기 위한 AbortController를 저장

  const validateId = (id) => /^[a-zA-Z0-9]{5,20}$/.test(id); // 아이디 형식 검사: 영문/숫자 5~20자만 허용

  const handleChange = (e) => { // 아이디 입력창이 바뀔 때마다 호출
    const raw = e.target.value; // 사용자가 입력한 원본 문자열
    const next = raw.trimStart();                     // 맨 앞쪽 공백은 자동 제거(실수 방지) - 필요시 .toLowerCase()로 소문자 강제도 가능
    setFormData({ ...formData, userId: next });      // 폼 상태에 반영
    if (idVerified) setIdVerified(false);             // 이미 인증완료였다면, 값이 바뀌었으므로 인증 상태를 해제
  };

  const openIdModal = (message, type) => { // 간단한 헬퍼: 모달을 열면서 메시지/타입을 설정
    setIdModalMessage(message);
    setIdModalType(type);
    setIsIdModalOpen(true);
  };

  const closeIdModal = () => { // 모달 닫기 헬퍼: 상태/메시지 초기화
    setIsIdModalOpen(false);
    setIdModalMessage('');
    setIdModalType('');
  };

  const checkIdAvailability = async () => { // "ID 중복검사" 버튼 클릭 시 호출
    const userId = (formData.userId || '').trim(); // 현재 입력된 아이디에서 양쪽 공백 제거(빈값 대비)
    if (checking) return;                              // 이미 요청 중이면 추가 호출 무시(연타 방지)
    if (!validateId(userId)) {                         // 형식이 맞지 않으면 서버에 가지 않고 즉시 안내
      openIdModal('아이디는 영어 대소문자 또는 숫자, 5~20자여야 합니다.', 'error');
      return;
    }

    try {
      setChecking(true);                               // 요청 시작: 버튼 상태를 "확인 중..."으로 바꾸는 데 사용

      // 이전 요청이 살아있다면 취소(빠르게 여러 번 누를 때 이전 요청을 끊어주기 위함)
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();        // 새 컨트롤러 생성
      const controller = abortRef.current;             // 가독성을 위해 지역 변수로 보관

      const timeout = setTimeout(() => controller.abort(), 8000); // 8초 이상 걸리면 타임아웃으로 취소

      const url = checkUrl || `${API_URL}/api/influencer/register/checkid`;
      const res = await fetch(url, { // 백엔드에 중복검사 요청
        method: 'POST',                                 // POST 메서드 사용
        headers: { 'Content-Type': 'application/json' },// JSON 전송
        body: JSON.stringify({ userId }),               // 요청 본문에 userId 전달
        signal: controller.signal                       // AbortController로 취소 가능하게 연결
      });
      clearTimeout(timeout);                            // 응답을 받았으므로 타임아웃 타이머 해제

      const data = await res.json().catch(() => ({}));  // JSON 파싱(실패해도 앱 죽지 않도록 대비)
      if (res.ok) {                                     // 200대 응답이면 사용 가능
        openIdModal('사용 가능한 아이디입니다. 사용하시겠습니까?', 'success'); // 확인 시 인증완료로 전환
      } else {                                          // 409 등 에러코드면 이미 사용 중
        openIdModal(data.message || '이미 존재하는 아이디입니다.', 'error'); // 서버 메시지 없으면 기본 문구
      }
    } catch (err) {                                     // 네트워크 오류/타임아웃/취소 등 예외 처리
      if (err.name === 'AbortError') {                  // 타임아웃이나 수동 abort의 경우
        openIdModal('요청이 지연되어 취소되었습니다. 잠시 후 다시 시도해주세요.', 'error');
      } else {
        openIdModal('서버 요청 실패', 'error');         // 기타 네트워크 실패
      }
    } finally {
      setChecking(false);                               // 요청 종료: 버튼/상태 복구
    }
  };

  return ( // 실제 화면에 그릴 JSX
    <div>
      <div className="id-verification__input-group">
        <input
          className="id-verification__input"
          name="userId"                                   // 입력 필드 이름(폼 전송/검증 시 식별자)
          placeholder="아이디"                             // 입력 힌트
          value={formData.userId}                         // 현재 아이디 값
          onChange={handleChange}                         // 입력 변경 시 상태 업데이트
          required                                        // 폼 필수 항목
          disabled={idVerified || loading || checking}    // 인증완료/전체로딩/검사중이면 수정 불가
          pattern="[A-Za-z0-9]{5,20}"                     // 브라우저 기본 패턴 검증(영문/숫자 5~20자)
          title="영문 대소문자/숫자 조합 5~20자"            // 패턴 미일치 시 안내 메시지
          autoComplete="username"                         // 브라우저 자동완성 힌트
          inputMode="latin"                               // 모바일 키보드 힌트(영문 위주)
          spellCheck={false}                              // 철자 검사 비활성화(아이디는 단어 아님)
        />
        <button
          className="id-verification__button"
          type="button"                                   // 일반 버튼(폼 제출 아님)
          onClick={checkIdAvailability}                   // 클릭 시 중복검사 요청
          disabled={idVerified || loading || checking}    // 인증완료/로딩/검사중엔 비활성화
        >
          {idVerified ? '인증완료' : (checking ? '확인 중...' : 'ID 중복검사')}
        </button>
      </div>

      {isIdModalOpen && (                               // 모달이 열렸을 때만 렌더링
        <div className="id-verification-modal">                         
          <div className="id-verification-modal__content">               
            <div className="id-verification-modal__message">{idModalMessage}</div>                   
            <div className="id-verification-modal__buttons">
            {idModalType === 'success' ? (              // 성공 모달: 확인/취소 두 버튼
              <>
                <button
                  type="button"
                  className="id-verification-modal__button id-verification-modal__button--confirm"
                  onClick={() => {                      // 사용자 확인 시
                    setIdVerified(true);               // 인증완료 상태로 전환(입력 잠금)
                    closeIdModal();                    // 모달 닫기
                  }}
                >
                  확인
                </button>
                <button type="button" className="id-verification-modal__button" onClick={closeIdModal}> {/* 마음 바뀌면 취소 */}
                  취소
                </button>
              </>
            ) : (                                       // 에러 모달: 닫기만 제공
              <button type="button" className="id-verification-modal__button" onClick={closeIdModal}>
                닫기
              </button>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IDVerification; // 다른 파일에서 이 컴포넌트를 사용할 수 있도록 내보내기